import {Tr} from "@/i18n/i18n";
import {Keys} from "@/constants/key";
import TextUtils from "@/utils/text-utils";
import {lazyInject} from "@/container";
import {TYPES} from "@/types";
import {SceneService} from "@/services/scene-service";
import {SceneName} from "@/model/scene-name";
import {CharacterName} from "@/store/player/types";
import SceneUtils from "@/utils/scene-utils";
import {Fonts} from "@/constants/font";
import TextButton from "@/scenes.ui/text-button";
import CU from "../utils/c-u";
import ListenerManager from "@/managers/listener-manager";
import {IntegrationUtils} from "@/utils/integration-utils";
import OnceCaller from "@/utils/once-caller";
import PlayerStore from "@/store/player";
import SecurityStore from "@/store/security";
import SoundUtils from "@/utils/sound-utils";
import {Settings} from "@/constants/settings";
import SpriteUtils from "@/utils/sprite-utils";
import {LoadUtils, PriorityType} from "@/utils/load-utils";
import {GameState} from "@/constants/game-state";
import Scene = Phaser.Scene;
import Sprite = Phaser.GameObjects.Sprite;
import ParticleEmitter = Phaser.GameObjects.Particles.ParticleEmitter;
import ParticleUtils from "@/utils/particle-utils";
import {LangSelectPanel} from "@/scenes.ui/lang-select-panel";
import {PlayerActionButton} from "@/scenes.ui/player-action-button";
import {InfoPanel} from "@/scenes.ui/info-panel";
import {Http} from "@/constants/http";
import {UnclosablePanel} from "@/scenes.ui/unclosable-panel";
import {Reloadable} from "@/scenes/reloadable";

export default class CharSelectionScene extends Scene implements Reloadable {

    private OFFSET_X: number = 160;
    private OFFSET_Y: number = -20;

    private canvas!: HTMLCanvasElement;
    private background!: Sprite;
    private bgAnim!: Array<ParticleEmitter>;

    @lazyInject(TYPES.SceneService)
    private sceneService!: SceneService;

    private completedSound: Phaser.Sound.BaseSound | undefined;
    private buttonClickSound: Phaser.Sound.BaseSound | undefined;

    constructor() {
        super({ key: 'CharSelectionScene' });

        let self = this;
        ListenerManager.setLoginSuccessfulCallback(() => {
            PlayerStore.fetchPlayerInfoCurrent().then(() => {
                self.sceneService!.startSceneInstant(self, SceneName.LOAD_SCENE, {});
            });
        });
    }

    public async preload() {
        SceneUtils.setCurrentScene(this);
        this.canvas = this.sys.game.canvas;
        CU.checkCanvasSize(this, () => {});
    }

    public update(): void {
        ListenerManager.callUpdateListeners();
    }

    private static isLongScreen() {
        if (CU.CANVAS_HEIGHT / CU.CANVAS_WIDTH > 911 / 620) {
            return true;
        } else {
            return false;
        }
    }

    public create(): void {
        let self = this;

        if (IntegrationUtils.isCrazyUser()) {
            if (GameState.REQUIRE_CRAZY_LOGIN) {
                const listener = (user: any) => {
                    console.log('Crazygames Auth Listener Fired')
                    // @ts-ignore
                    window.CrazyGames.SDK.user.getUserToken().then((token: any) => {
                        Http.CRAZY_ID = token;
                        if (token) {
                            GameState.REQUIRE_CRAZY_LOGIN = false;
                        }
                        SecurityStore.authenticateCrazy().then(() => {
                            SecurityStore.authenticateCrazy().then(() => {
                                PlayerStore.fetchPlayerInfoCurrent().then(() => {
                                    self.sceneService!.startSceneInstant(self, SceneName.LOAD_SCENE, {});

                                    console.log('Crazygames Auth Listener Removed');
                                    // @ts-ignore
                                    window.CrazyGames.SDK.user.removeAuthListener(listener);
                                });
                            });
                        });
                    });
                };
                console.log('Crazygames Auth Listener Added');
                // @ts-ignore
                window.CrazyGames.SDK.user.addAuthListener(listener);

                new UnclosablePanel(this, this.canvas, 'UPS!', '', 'This multiplayer game requires a Crazygames account.', () => {
                    // @ts-ignore
                    window.CrazyGames.SDK.user.showAuthPrompt();
                }, 'REGISTER').openAnimated();
            }
        }

        if (IntegrationUtils.isKongUser()) {
            if (GameState.REQUIRE_KONG_LOGIN) {
                new UnclosablePanel(this, this.canvas, 'UPS!', '', 'This multiplayer game requires a Kongregate account', () => {
                    // @ts-ignore
                    window.kongregate.services.showRegistrationBox();
                }, 'REGISTER').openAnimated();
            }
        }

        this.bgAnim = LoadUtils.getCurrentStyleAnim(this, 0.3);

        let longScreen = CharSelectionScene.isLongScreen();

        if (longScreen) {
            this.OFFSET_Y = 10;
        }

        this.completedSound = SoundUtils.addSound(this, Keys.SOUND_COMPLETED);
        this.buttonClickSound = SoundUtils.addSound(this, Keys.SOUND_BUTTON_CLICK, {volume: Settings.SHORT_TRACK_VOLUME});

        this.sceneService!.fadeBlackout(this, this.canvas!.width, this.canvas!.height);

        this.background = SpriteUtils.addSprite(this, 0, -(800 - this.canvas.height / 2 / CU.S) + 25, Keys.IMG_CHAR_SELECTION_BACKGROUND)
            .setOrigin(0, 0);

        let loginVisible = !IntegrationUtils.isExternalUser();

        SpriteUtils.addSprite(this, 0, this.canvas.height / CU.S + 10, Keys.UI_LOGIN_BUTT_BG).setOrigin(0, 1).setVisible(loginVisible);

        TextUtils.addTextAdvanced(this, Tr('uzhe.est.akkaunt'),
            this.canvas.width / 2 / CU.S, this.canvas.height / CU.S - 80, '#fffbf2',
            15, 'normal', 500, 'center', 1, Fonts.ARIAL_MT).setVisible(loginVisible).setDepth(2);

        let loginButton = new TextButton(this, 300, this.canvas.height / CU.S - 34, Keys.UI_LOGIN_BUTT, Tr('voyti')).setVisible(loginVisible).setDepth(2);
        loginButton.setCallback(() => {
            CU.blockCanvas(this);
            ListenerManager.callLoginCallback();
        });
        this.add.existing(loginButton).setVisible(loginVisible);

        if (IntegrationUtils.isI18NEnabled() && !IntegrationUtils.isCrazyUser() && !IntegrationUtils.isKongUser()) {
            // СМЕНА ЯЗЫКА
            let lang_b_x = this.canvas.width / CU.S - 45;
            let lang_b_y = this.canvas.width / CU.S - 523;

            let okClickCallback = () => {
                let langPanel = new LangSelectPanel(this, this.canvas!, () => {
                    this.switchSceneInstant(SceneName.CHAR_SELECTION_SCENE, {});
                }, Tr('lan'), Tr('select.lang'), false);
                langPanel.openAnimated();
            };

            const langButton = new PlayerActionButton(this, lang_b_x, lang_b_y, Tr('lan'),
                undefined, Keys.UI_LANG_BUTT,0, okClickCallback
            );
            this.add.existing(langButton).setVisible(true);
        }

        let maleImage = SpriteUtils.addSprite(this, this.canvas!.width / 2 / CU.S - this.OFFSET_X,
            this.canvas!.height / 2 / CU.S + 210, Keys.profileCreature(Keys.MAIN_HERO_MAN), undefined, 0.7).setDepth(2);

        let femaleImage = SpriteUtils.addSprite(this, this.canvas!.width / 2 / CU.S + this.OFFSET_X + 20,
            this.canvas!.height / 2 / CU.S + 200, Keys.profileCreature(Keys.MAIN_HERO_WOMAN), undefined, 0.7).setDepth(2);

        SpriteUtils.addSprite(this, maleImage.x / CU.S - 30, maleImage.y / CU.S + 23, Keys.profileCreature(Keys.IMG_MAIN_HERO_MALE_CAT), undefined, 0.4).setDepth(3);;

        SpriteUtils.addSprite(this, femaleImage.x / CU.S + 10, femaleImage.y / CU.S + 30, Keys.profileCreature(Keys.IMG_MAIN_HERO_FEMALE_CAT), undefined, 0.4).setDepth(3);

        let selectMaleBackground = SpriteUtils.addSprite(this, this.canvas!.width / 2 / CU.S - this.OFFSET_X,
            this.canvas!.height / 2 / CU.S + this.OFFSET_Y, Keys.IMG_CHAR_SELECTION_WINDOW);

        let selectFemaleBackground = SpriteUtils.addSprite(this, this.canvas!.width / 2 / CU.S + this.OFFSET_X,
            this.canvas!.height / 2 / CU.S + this.OFFSET_Y, Keys.IMG_CHAR_SELECTION_WINDOW);

        TextUtils.addTextAdvanced(this, Tr('vibrat'),
            selectMaleBackground.x / CU.S, selectMaleBackground.y / CU.S + selectMaleBackground.height/ 2 - 35, '#efe5b0',
            25, 'normal', selectMaleBackground.width, 'center', 1, Fonts.MYRIADPRO_BOLD).setDepth(2);

        TextUtils.addTextAdvanced(this, Tr('vibrat'),
            selectFemaleBackground.x / CU.S, selectFemaleBackground.y / CU.S + selectFemaleBackground.height / 2 - 35, '#efe5b0',
            25, 'normal', selectFemaleBackground.width, 'center', 1, Fonts.MYRIADPRO_BOLD).setDepth(2);

        selectMaleBackground.setInteractive().on('pointerdown', (pointer: any) => {
            this.selectCharacter(CharacterName.MAN_COMPANION_2, pointer.x, pointer.y) });

        selectFemaleBackground.setInteractive().on('pointerdown', (pointer: any) => {
            this.selectCharacter(CharacterName.WOMAN_COMPANION_1, pointer.x, pointer.y) });

        SpriteUtils.addSprite(this, this.canvas!.width / 2 / CU.S, 100, Keys.EDU_BLACKOUT)
            .setOrigin(0.5, 0.5);

        if (longScreen) {
            let textY = 82;

            TextUtils.addTextAdvanced(this, Tr('viberi.geroja.kotoriy.bolshe.tebe.podhodit'),
                170, textY, '#ffffff', 15, 'bold',
                380, 'left', 2, Fonts.MYRIADPRO_BOLD);


            SpriteUtils.addSprite(this, 20, textY - 60, 'rab-edu')
                .setOrigin(0, 0);
        } else {

            let textY = (this.canvas!.height / 2 / CU.S - 650 / 2 - this.OFFSET_Y) / 2 - 10;
            TextUtils.addTextAdvanced(this, Tr('viberi.geroja.kotoriy.bolshe.tebe.podhodit'),
                300, textY, '#ffffff', 15, 'normal',
                380, 'center', 2, Fonts.MYRIADPRO_BOLD).setDepth(2);


            SpriteUtils.addSprite(this, 20, textY - 40, 'rab-edu')
                .setOrigin(0, 0).setScale(0.6);
        }

        SoundUtils.play(this.completedSound);

        if (!Settings.LOGGED_GAME_STARTED) {
            Settings.LOGGED_GAME_STARTED = true;
            PlayerStore.logGameStarted(false);
        }

        LoadUtils.prioritizedLoad(this, PriorityType.NEW_PLAYER);
        GameState.loadingType = PriorityType.ROAD;
    }

    private switchSceneInstant(sceneName: SceneName, params: any) {
        this.stopEverithingInScene();
        this.sceneService.startSceneWithParams(this, params, sceneName, this.canvas!.width, this.canvas!.height);
    }

    private stopEverithingInScene() {
        if (this.completedSound) {
            if (this.completedSound) {
                SoundUtils.stop(this.completedSound);
            }
        }
    }

    private selectCharacter(characterAndCompanion: CharacterName, x: number, y: number){
        SoundUtils.play(this.buttonClickSound);
        ParticleUtils.showParticleWave(this, x, y, 1.4, 3);
        if (IntegrationUtils.isMystoreUser()) {
            this.doSelectCharacterMystore(characterAndCompanion);
        } else {
            this.doSelectCharacter(characterAndCompanion);
        }
    }

    private doSelectCharacter(characterAndCompanion: CharacterName){
        OnceCaller.doOnce("registerTrial", () => {
            this.bgAnim.forEach(emitter => {
                emitter.killAll();
                emitter.destroy();
            });

            this.doStartGame(characterAndCompanion, 75);
        });
    }

    private doSelectCharacterMystore(characterAndCompanion: CharacterName){
        const self = this;
        OnceCaller.doOnce("registerTrialMystore", () => {
            IntegrationUtils.registerMystoreUser((uid: string, hash: string) => {
                SecurityStore.authenticateMystore(uid).then(() => {
                    self.doStartGame(characterAndCompanion, 0);
                })
            });
        });
    }

    private doStartGame(characterAndCompanion: CharacterName, delay: number){
        let self = this;

        let faded = false;
        let responseRecieved = false;

        let black = this.sceneService.fadeInBlackout(this, this.canvas!.width, this.canvas!.height, () => {
            faded = true;
            if (responseRecieved) {
                self.sceneService!.startSceneInstant(self, SceneName.ROAD_SCENE);
            }
        }, delay, 800);

        SecurityStore.registerTrial(characterAndCompanion).then(() => {
            PlayerStore.fetchPlayerInfoCurrent().then(() => {
                responseRecieved = true;
                if (faded) {
                    self.sceneService!.startSceneInstant(self, SceneName.ROAD_SCENE);
                }
            });
        }).catch((e: Error) => {
            PlayerStore.logClientError('Blackout error: ' + e);
            if (black) {
                this.sceneService.cancelBackground(black);
            }
            ListenerManager.callConnectionLostCallback();
        });
    }

    public reload(): void {
        this.switchSceneInstant(SceneName.CHAR_SELECTION_SCENE, {});
    }
}
